import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "components/app/dialog/dialog";

import { Button } from "components/ui/button";
import { ScrollArea, ScrollBar } from "components/ui/scroll-area";
import { Loader, X } from "lucide-react";

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "components/ui/form";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Textarea } from "components/ui/textarea";
import { Input } from "components/ui/input";

interface DialogProps {
  isDailogAddPrinter: boolean;
  setIsDailogAddPrinter: (value: boolean) => void;
  setItemPrinter: any;
  // updateImage: (newImage: File) => void; // Add this to update the image in parent component
}

export default function DailogAddPrinter({
  isDailogAddPrinter,
  setIsDailogAddPrinter,
  setItemPrinter,
}: // updateImage, // Add this prop to pass the updated image back to the parent component
DialogProps) {
  const { token } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [loadingSubmitAddPrinter, setLoadingSubmitAddPrinter] = useState<boolean>(false);

  const shopId = searchParams.get("id");

  const FormSchema = z.object({
    local_description: z.string().min(1, {
      message: "Descrption is required*",
    }),
    ip_address: z.string().min(1, {
      message: "Address is required*",
    }),
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      local_description: "",
      ip_address: "",
    },
  });

  const onSubmitAddPrinter = async (data: z.infer<typeof FormSchema>) => {
    setLoadingSubmitAddPrinter(true);
    try {
      let url: string = "";
      url = RouteApi.submitInsertShopPrinter;
      url += `token=${token}`;
      const payload = {
        shop_id: shopId,
        local_description: data.local_description,
        ip_address: data.ip_address,
      };

      const response = await RequestApi(url, { data: payload });
      if (response.status === "success") {
        setLoadingSubmitAddPrinter(false);
        setIsDailogAddPrinter(false);
        if (response.user_shop_printer) {
          setItemPrinter((prevItemGroups: any) => {
            const printerData = response.user_shop_printer;

            // Filter out unwanted IDs (if needed)
            const filteredData = (prevItemGroups || []).filter((item: any) => item.id !== 9 && item.id !== 10);

            // Replace existing data or add new entry
            const updatedData = filteredData.map((item: any) => (item.id === printerData.id ? { ...item, ...printerData } : item));

            if (!updatedData.some((item: any) => item.id === printerData.id)) {
              updatedData.push(printerData);
            }

            return updatedData; // No "data:" key, just returning updatedData
          });
        }
        form.reset();
      }
    } catch (error) {
      AppLog.show({
        message: `[onSubmitAddPrinter]`,
        status: StatusMsg.error,
        error,
      });
      // setLoadingSubmit(false);
      setLoadingSubmitAddPrinter(false);
    } finally {
    }
  };

  return (
    <Dialog open={isDailogAddPrinter}>
      <DialogContent isShowBtnXClose={false} className="custom-dialog-class flex flex-col justify-between overflow-hidden">
        {loadingSubmitAddPrinter && <div className="bg-black/50 flex items-center justify-center w-full h-[585px] absolute top-0 left-0 z-10 rounded-md"></div>}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmitAddPrinter)} className="text-start space-y-4">
            <DialogHeader>
              <DialogTitle>Add New Kitchen</DialogTitle>
              <DialogDescription>Input kitchen description and kitchen printer IP address, click "Save" when you finished.</DialogDescription>
              <div
                className="absolute flex items-center justify-center right-4 top-1 z-40 cursor-pointer rounded-full bg-gray-200 dark:bg-accent w-[28px] h-[28px]"
                onClick={() => {
                  form.reset();
                  setIsDailogAddPrinter(false);
                }}
              >
                <X />
              </div>
            </DialogHeader>
            <ScrollArea className="mt-2 w-full gap-3 ">
              <div className="flex flex-col py-[10px]">
                <div className="sticky top-0 left-0 bg-white dark:bg-[#020817] px-1">
                  <FormField
                    control={form.control}
                    name="local_description"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-white">
                          <FormLabel className="text-black text-sm dark:text-white">
                            Description <span className="text-red-600">*</span>
                          </FormLabel>
                        </div>

                        <FormControl>
                          <Textarea {...field} />
                        </FormControl>
                        <div className="text-md">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="ip_address"
                    render={({ field }) => (
                      <FormItem className="">
                        <div className="text-white">
                          <FormLabel className="text-black text-sm dark:text-white">
                            IP Address <span className="text-red-600 py-[10px]">*</span>
                          </FormLabel>
                        </div>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <div className="text-md">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <ScrollBar orientation="vertical" />
            </ScrollArea>
            <DialogFooter>
              <div className="flex justify-between w-full gap-3">
                <Button className={`w-full text-white mt-3 bg-blue-600 hover:bg-blue-500`}>{loadingSubmitAddPrinter ? <Loader className="animate-spin" /> : "Save"}</Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
